import React, { useState, useEffect } from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  startOfYear,
  endOfWeek,
  addDays,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  addYears,
  subYears,
} from "date-fns";
import moment from "moment";
import "./NewCalender.scss";
import { formatDateInTimeZoneUTC, FormatUSD } from "../../../utilities/string";
import { ReactComponent as LeftIcon } from "../../../assets/icons/NewIcons/LeftIcon.svg";
import { ReactComponent as RightIcon } from "../../../assets/icons/NewIcons/RightIcon.svg";

const NewCalender = ({
  data,
  setSelectedDateValue,
  selectedDateValue,
  setDateWiseData,
  setSelectedDate,
  selectedDate,
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [events, setEvents] = useState({});
  const [month, setMonth] = useState("month");

  // Process trade data into events for the calendar
  useEffect(() => {
    if (data) {
      const tradingProfits = {};
      data?.forEach((trade) => {
        const tradeDate = formatDateInTimeZoneUTC(trade?.date, "DD-MM-YYYY");
        if (!tradingProfits[tradeDate]) {
          tradingProfits[tradeDate] = {
            trades: [],
            totalProfit: 0,
            tradeCount: 0,
          };
        }
        tradingProfits[tradeDate].trades.push(trade);
        const totalTradeProfit = (Number(trade?.profit) || 0) + (Number(trade?.swap) || 0);
        tradingProfits[tradeDate].totalProfit += totalTradeProfit;
        tradingProfits[tradeDate].tradeCount += 1;
      });
      setEvents(tradingProfits);
      setDateWiseData(tradingProfits);
    }
  }, [data, setDateWiseData]);

  // Render year view with monthly profit/loss
  const renderYearView = () => {
    const yearStart = startOfYear(currentMonth);
    const months = [];

    for (let i = 0; i < 12; i++) {
      const monthStart = addMonths(yearStart, i);
      const formattedMonth = format(monthStart, "MMMM");
      const formattedDate = format(monthStart, "yyyy-MM-dd");
      const monthName = moment(formattedDate, "YYYY-MM-DD").format("MMMM");

      let totalProfitOrLoss = 0;
      let result = {
        totalProfitOrLoss: 0,
        monthName: null,
      };

      const checkYear = moment(formattedDate).year();
      const checkMonth = moment(formattedDate).month();

      Object.keys(events)?.forEach((key) => {
        const [day, month, year] = key.split("-");
        const keyYear = parseInt(year, 10);
        const keyMonth = parseInt(month, 10) - 1; // Convert to 0-based month index

        if (keyYear === checkYear && keyMonth === checkMonth) {
          result.monthName = moment(formattedDate).format("MMMM");
          result.totalProfitOrLoss += events[key].totalProfit;
        }
      });

      const borderColor = result.totalProfitOrLoss < 0
        ? "#cb2f2e" // Red for loss
        : result.totalProfitOrLoss > 0
        ? "rgb(100, 193, 68)" // Green for profit
        : "transparent"; // No border for zero

      months.push(
        <div
          key={i}
          className={`month-cell cell col ${
            result.totalProfitOrLoss !== 0 ? "month-with-events" : ""
          }`}
          onClick={() => {
            setCurrentMonth(monthStart);
            setMonth("month");
          }}
          style={{
            border: `1px solid ${borderColor}`,
          }}
        >
          <span>{formattedMonth}</span>
          {result.totalProfitOrLoss !== 0 && (
            <div className="month-events">
              {result.totalProfitOrLoss > 0
                ? `Profit: ${FormatUSD(result.totalProfitOrLoss)}`
                : `Loss: ${FormatUSD(Math.abs(result.totalProfitOrLoss))}`}
            </div>
          )}
        </div>
      );
    }
    return <div className="year-view">{months}</div>;
  };

  const renderHeader = () => (
    <div className="calendar-header">
      <div className="calendar-header_left">
        <button onClick={month === "month" ? prevMonth : prevYear}>
          <LeftIcon />
        </button>
        <span>
          {month === "month"
            ? format(currentMonth, "MMMM yyyy") // Show full month and year
            : format(currentMonth, "yyyy")}{" "}
          {/* Show only the year */}
        </span>
        <button onClick={month === "month" ? nextMonth : nextYear}>
          <RightIcon />
        </button>
      </div>
      <div className="calendar-header_right">
        <button
          className={`${month === "month" ? "active" : ""}`}
          onClick={() => setMonth("month")}
        >
          Month
        </button>
        <button
          className={`${month === "year" ? "active" : ""}`}
          onClick={() => {
            setMonth("year");
            setSelectedDateValue(false);
          }}
        >
          Year
        </button>
      </div>
    </div>
  );

  const renderDays = () => {
    const days = [];
    const startDate = startOfWeek(currentMonth);

    for (let i = 0; i < 7; i++) {
      days.push(
        <div key={i} className="day-name">
          {format(addDays(startDate, i), "E")}
        </div>
      );
    }

    return <div className="days-row">{days}</div>;
  };

  const renderCells = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const rows = [];
    let days = [];
    let day = startDate;

    while (day <= endDate) {
      const formattedDate = format(day, "dd-MM-yyyy");
      const cloneDay = day;
      const eventForDay = events[formattedDate];
      const totalProfitOrLoss = eventForDay ? eventForDay.totalProfit : 0;

      days.push(
        <div
          className={`col cell ${
            !isSameMonth(day, monthStart) 
              ? "disabled"
              : isSameDay(day, selectedDate)
              ? "selected"
              : ""
          } ${
            eventForDay &&
            (eventForDay?.totalProfit > 0
              ? "profit-event"
              : eventForDay?.totalProfit < 0
              ? "loss-event"
              : "normal-event")
          }`}
          key={day}
          onClick={() => onDateClick(cloneDay)}
        >
          <span
            className={`number ${
              eventForDay?.totalProfit > 0
                ? "profit_day"
                : eventForDay?.totalProfit < 0
                ? "loss_day"
                : "normal_day"
            }`}
          >
            {format(day, "d")}
          </span>
          {eventForDay && (
            <div className="event-title">
              <span
                className={`${
                  totalProfitOrLoss > 0
                    ? "profit_val"
                    : totalProfitOrLoss === 0
                    ? "normal_val"
                    : "loss_val"
                }`}
              >
                {FormatUSD(totalProfitOrLoss)}
              </span>
            </div>
          )}
        </div>
      );
      day = addDays(day, 1);

      if (days.length === 7) {
        rows.push(
          <div className="row" key={day}>
            {days}
          </div>
        );
        days = [];
      }
    }

    if (days.length > 0) {
      rows.push(
        <div className="row" key={day}>
          {days}
        </div>
      );
    }

    return <div className="body">{rows}</div>;
  };

  const nextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const prevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const nextYear = () => {
    setCurrentMonth(addYears(currentMonth, 1));
  };

  const prevYear = () => {
    setCurrentMonth(subYears(currentMonth, 1));
  };

  const onDateClick = (day) => {
    setSelectedDate(day);
    const utcDate = moment(day).format("DD-MM-YYYY");
    const tradeValues = data?.filter(
      (item) => formatDateInTimeZoneUTC(item?.date) === utcDate
    );
    setSelectedDateValue(tradeValues);
  };

  const calculateTotalProfitLoss = (trades) => {
    if (!trades || trades.length === 0) return 0;
    return trades.reduce((total, trade) => {
      const profit = Number(trade?.profit || 0);
      const swap = Number(trade?.swap || 0);
      return total + profit + swap;
    }, 0);
  };

  return (
    <div className="calendar">
      {renderHeader()}
      <div
        className={`main_calendar_container ${
          selectedDateValue ? "grid" : "auto"
        }`}
      >
        <div className="main_calendar_container_inner">
          {month === "year" ? renderYearView() : renderDays()}
          {month === "month" && renderCells()}
        </div>

        {selectedDateValue && (
          <div className="selected_trade_con">
            <div className="selected_trade_con_header">
              <h4>
                {selectedDateValue[0]?.date ? (
                 format(selectedDate,"dd-MM-yyyy")
                ) : (
                  <span>No transactions on this date</span>
                )}
              </h4>
              <div
                onClick={() => setSelectedDateValue(false)}
                style={{ cursor: "pointer" }}
              >
                X
              </div>
            </div>

            <span style={{ textWrap: "nowrap" }} className="trade_date">
              {selectedDateValue[0]?.date
                ? `Trade Summary, ${format(selectedDate,"dd-MM-yyyy")}`
                : ""}
            </span>

            <div className="profit_loss_info">
              {selectedDateValue?.map((trade, index) => {
                const totalTradeProfit = (Number(trade?.profit) || 0) + (Number(trade?.swap) || 0);
                return (
                  totalTradeProfit !== 0 && (
                    <div key={index} className="profit_loss_info_inner">
                      <div
                        className={
                          totalTradeProfit < 0
                            ? "loss_dot"
                            : totalTradeProfit === 0
                            ? "normal"
                            : "profit_dot"
                        }
                      ></div>
                      <p>
                        {totalTradeProfit < 0
                          ? `Loss of ${FormatUSD(Math.abs(totalTradeProfit))}`
                          : totalTradeProfit === 0
                          ? `No profit/loss - ${FormatUSD(0)}`
                          : `Profit of ${FormatUSD(totalTradeProfit)}`}
                      </p>
                    </div>
                  )
                );
              })}
            </div>

            <div className="profit_loss_info_summary">
              <span>Overall Result</span>
              {selectedDateValue?.length > 0 ? (
                <p
                  className={
                    calculateTotalProfitLoss(selectedDateValue) < 0
                      ? "loss_val"
                      : calculateTotalProfitLoss(selectedDateValue) > 0
                      ? "profit_val"
                      : "normal_val"
                  }
                >
                  {calculateTotalProfitLoss(selectedDateValue) < 0
                    ? `Total Loss: ${FormatUSD(
                        Math.abs(calculateTotalProfitLoss(selectedDateValue))
                      )}`
                    : calculateTotalProfitLoss(selectedDateValue) > 0
                    ? `Total Profit: ${FormatUSD(
                        calculateTotalProfitLoss(selectedDateValue)
                      )}`
                    : `No Profit/Loss: ${FormatUSD(0)}`}
                  {selectedDateValue[0]?.date
                    ? ` on ${format(selectedDate,"dd-MM-yyyy")}`
                    : ""}
                </p>
              ) : (
                <p className="normal_val">No trades recorded on this date</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewCalender;